export interface SpriteBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ALPHA_THRESHOLD = 10;

/** Finds the opaque bounding box of a sprite frame, in coordinates local to
 *  the source rect. Returns null when it cannot be measured — a tainted
 *  canvas, no 2d context, or (jsdom's default) an all-transparent readback —
 *  so callers can fall back to the frame box instead of rendering nothing. */
export function measureSpriteBounds(
  img: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
): SpriteBounds | null {
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  try {
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    const { data, width, height } = ctx.getImageData(0, 0, sw, sh);

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha !== undefined && alpha > ALPHA_THRESHOLD) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) return null;

    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  } catch {
    return null;
  }
}
